import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../shared/lib/supabase';

type ScoresheetLog = {
  id: string;
  match_id: string;
  team_id: string | null;
  stat_type_id: string;
  match_minute: number | null;
  action: 'increment' | 'decrement';
  new_value: number;
  timestamp: string;
  user_id?: string | null;
  stat_types?: { stat_key: string; display_name: string } | null;
  player_id?: string | null;
  position_data?: any | null;
  player?: { full_name: string; position?: any } | null;
};

interface ScoresheetLogsProps {
  matchId: string;
  teamIdToName?: Record<string, string>;
}

const MAX_LOGS = 10;

const formatTime = (ts: string) => new Date(ts).toLocaleTimeString();

const codeColorForAction = (action: 'increment' | 'decrement') =>
  action === 'increment' ? 'text-green-400' : 'text-red-400';

const ScoresheetLogs: React.FC<ScoresheetLogsProps> = ({ matchId, teamIdToName = {} }) => {
  const [logs, setLogs] = useState<ScoresheetLog[]>([]);
  const latestFetch = useRef<number>(0);

  // Fetch latest logs (joined for names)
  const loadLatest = async () => {
    latestFetch.current = Date.now();
    const { data, error } = await supabase
      .from('scoresheet_logs')
      .select(
        `id, match_id, team_id, stat_type_id, match_minute, action, new_value, timestamp, user_id, player_id, position_data,
         stat_types(stat_key, display_name),
         player:players!player_id(full_name, position)`
      )
      .eq('match_id', matchId)
      .order('timestamp', { ascending: false })
      .limit(MAX_LOGS);
    if (!error) setLogs((data as unknown as ScoresheetLog[]) || []);
  };

  // Initial load
  useEffect(() => {
    if (matchId) loadLatest();
  }, [matchId]);

  // Realtime subscription + fallback polling
  useEffect(() => {
    if (!matchId) return;
    const channel = supabase
      .channel('scoresheet-logs-view')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'scoresheet_logs', filter: `match_id=eq.${matchId}` },
        async () => {
          // re-fetch latest to include joins and keep order consistent
          await loadLatest();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'scoresheet', filter: `match_id=eq.${matchId}` },
        async () => {
          await loadLatest();
        }
      )
      .subscribe();
    // Fallback polling every 8s in case realtime misses events (optimized to reduce egress)
    // Real-time subscriptions handle most updates, so polling can be less frequent
    const poll = setInterval(loadLatest, 8000);
    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [matchId]);

  return (
    <div className="mt-6 rounded-xl border border-gray-800 bg-black overflow-hidden">
      <div className="px-4 py-2 bg-gray-900 border-b border-gray-800 flex items-center justify-between">
        <div className="text-xs font-mono text-green-400">// scoresheet realtime logs (latest 10)</div>
        <div className="text-[10px] font-mono text-gray-400">latest first</div>
      </div>
      <div className="font-mono text-xs leading-5">
        {logs.length === 0 ? (
          <div className="px-4 py-3 text-gray-500">no logs yet…</div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="px-4 py-1 border-b border-gray-900 hover:bg-gray-950">
              <span className="text-gray-500">[{formatTime(log.timestamp)}]</span>{' '}
              {/* match id omitted per UI requirement */}
              <span className="text-purple-400">{log.stat_types?.stat_key || log.stat_type_id}</span>{' '}
              <span className={codeColorForAction(log.action)}>{log.action === 'increment' ? '+1' : '-1'}</span>{' '}
              <span className="text-gray-400">→</span>{' '}
              <span className="text-gray-200">{log.new_value}</span>{' '}
              {log.match_minute != null && (
                <span className="text-gray-500">(@{log.match_minute}') </span>
              )}
              {log.team_id && (
                <>
                  <span className="text-gray-500">team:</span>
                  <span className="text-yellow-400">{teamIdToName[log.team_id] || log.team_id.slice(0, 6)}</span>
                </>
              )}
              {log.player && (
                <>
                  <span className="text-gray-500"> player:</span>
                  <span className="text-cyan-400">{log.player.full_name}</span>
                </>
              )}
              {log.position_data && (
                <>
                  <span className="text-gray-500"> pos:</span>
                  <span className="text-gray-400">
                    {Object.entries(log.position_data).slice(0,5).map(([k,v],i)=> (
                      <span key={k}>{i>0?', ':''}{k}:{typeof v==='object'?JSON.stringify(v):String(v)}</span>
                    ))}
                    {Object.keys(log.position_data).length>5?' …':''}
                  </span>
                </>
              )}
            </div>
          ))
        )}
      </div>
      <div className="px-4 py-1 bg-gray-900 text-[10px] text-gray-500 border-t border-gray-800">
        updating in realtime from DB…
      </div>
    </div>
  );
};

export default ScoresheetLogs;


